import React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, LayoutDashboard, Plus } from "lucide-react";
import { useBillingUsage, useBots } from "../hooks/useApi";
import { Skeleton } from "@/components/ui/skeleton";
import { useMinLoadingTime } from "@/hooks/useMinLoadingTime";
import PendingInvitations from "../components/PendingInvitations";
import Consumo from "../components/billing/Consumo";
import { SchedaAssistente } from "../components/assistenti/SchedaAssistente";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { GRIGIO, IntestazioneCasella, SCURO } from "../components/integrations/casella";
import { api } from "../api/client";
import { GRATUITO } from "../components/billing/piani";
import { useTenant } from "../contexts/TenantContext";
import { useTranslation } from "../hooks/useTranslation";
import { useControlloLimite } from "../components/billing/AvvisoLimitePiano";

// Nella Dashboard al massimo due righe di assistenti: gli altri sono in Assistenti.
const ASSISTENTI_IN_DASHBOARD = 6;

/** Il titolo di una sezione: una riga in grassetto e una di spiegazione. */
function TitoloSezione({ titolo, descrizione, azioni }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-[#020617]">{titolo}</h2>
        {descrizione ? <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">{descrizione}</p> : null}
      </div>
      {azioni ? <div className="flex shrink-0 flex-wrap gap-2">{azioni}</div> : null}
    </div>
  );
}

/**
 * La Dashboard: il piano con i consumi dell'organizzazione e gli assistenti.
 * Senza assistenti e senza inviti da accettare si va dritti a crearne uno.
 */
const UserHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Al limite di assistenti del piano, chi puo' comprare vede subito i
  // piani; gli altri vanno al wizard, che lo spiega (LimiteAssistenti).
  const { controlla } = useControlloLimite();
  const nuovoAssistente = controlla({ limite: "widgets_max" }, () => navigate("/bots/new"));
  const { data: bots, isLoading, error } = useBots();
  const { activeTenant, tenants, canManageBilling, isLoading: tenantLoading, pass, adminDiSistema } = useTenant();

  // Prima di mandare a creare il primo assistente: magari c'e' un invito da accettare.
  const inviti = useQuery({
    queryKey: ["pendingInvitations"],
    queryFn: async () => {
      try {
        return await api.get("/invites/pending");
      } catch (e) {
        console.error("Error fetching invites:", e);
        // Un errore qui non deve bloccare il resto della pagina.
        return { invitations: [], total: 0 };
      }
    },
  });

  // Piano, limiti e consumi dell'organizzazione attiva: gli stessi numeri con
  // cui l'API decide se si puo' aggiungere qualcosa.
  const abbonamento = useBillingUsage();

  const showSkeleton = useMinLoadingTime(isLoading || abbonamento.isLoading);
  const conInviti = inviti.data?.invitations?.length > 0;

  if (!isLoading && inviti.isSuccess && Array.isArray(bots) && bots.length === 0 && !conInviti) {
    // L'amministratore di sistema non e' un cliente da accompagnare a creare
    // il primo assistente: il suo posto e' la pagina Clienti. Dentro un
    // cliente come PingUp, invece, vede quello che vedrebbe il cliente.
    return <Navigate to={adminDiSistema && !pass ? "/admin" : "/bots/new"} replace />;
  }

  const limiti = abbonamento.data?.effective?.limits || {};
  const piano = abbonamento.data?.plan;
  const nomePiano = piano ? (piano.key === GRATUITO ? t("onboardingWizard.planFree", "Gratuito") : piano.name) : null;
  const usi = abbonamento.data?.usage || {};
  const assistenti = Array.isArray(bots) ? bots : [];
  const inVista = assistenti.slice(0, ASSISTENTI_IN_DASHBOARD);

  // Senza un'organizzazione attiva (es. invitato che non ha ancora accettato,
  // o rimosso dall'unica che aveva) non ci sono ne' piano ne' assistenti:
  // solo gli inviti da accettare.
  if (!tenantLoading && !activeTenant && !tenants?.length) {
    return (
      <div className="relative isolate" style={VARIABILI_SEZIONE}>
        <BrandStyles />
        <IntestazioneCasella icona={LayoutDashboard} titolo={t("common.dashboard")} />
        <div className="pu-fade-up mx-auto max-w-[1400px] space-y-10 pb-10">
          {conInviti ? <PendingInvitations /> : null}
          <BrandCard className="flex flex-col items-center px-6 py-12 text-center">
            <p className="text-base font-semibold text-[#020617]">{t("dashboard.noOrganizationTitle")}</p>
            <p className="mt-1 max-w-md text-sm text-[#64748B]">
              {conInviti ? t("dashboard.noOrganizationWithInvites") : t("dashboard.noOrganizationText")}
            </p>
          </BrandCard>
        </div>
      </div>
    );
  }

  const creaAssistente = (
    <button type="button" className={SCURO} onClick={nuovoAssistente}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      {t("dashboard.createAssistant")}
    </button>
  );

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />
      <IntestazioneCasella
        icona={LayoutDashboard}
        titolo={t("common.dashboard")}
        dettagli={
          activeTenant?.name ? (
            <span className="truncate text-sm text-[#475569]">{activeTenant.name}</span>
          ) : null
        }
        azioni={creaAssistente}
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-10 pb-10">
        {!isLoading && !inviti.isLoading && conInviti ? <PendingInvitations /> : null}

        {!showSkeleton && error ? (
          <div role="alert" className="flex items-start gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-red-800">{t("dashboard.errorLoading")}</p>
              <p className="mt-1 text-sm text-red-700">{error.message}</p>
            </div>
            <button type="button" className={GRIGIO} onClick={() => window.location.reload()}>
              {t("dashboard.tryAgain")}
            </button>
          </div>
        ) : null}

        {/* Il piano: i consumi di tutta l'organizzazione. */}
        <section className="space-y-4">
          <TitoloSezione
            titolo={
              <>
                {t("dashboard.planTitle")}
                {nomePiano ? (
                  <span className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#334155]">
                    {nomePiano}
                  </span>
                ) : null}
              </>
            }
            descrizione={t("dashboard.planDescription")}
            azioni={
              canManageBilling ? (
                <Link to="/billing" className={GRIGIO}>
                  {t("dashboard.seePlan")}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              ) : null
            }
          />
          <div className="grid gap-4 lg:grid-cols-3">
            {showSkeleton ? (
              Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[130px] rounded-[14px]" />)
            ) : (
              <>
                <Consumo etichetta={t("dashboard.usageReplies")} usato={usi.messages_used || 0} limite={limiti.messages_per_month} ripartono={usi.messages_period_end} />
                <Consumo etichetta={t("dashboard.usageSources")} usato={usi.sources_count || 0} limite={limiti.sources_max} />
                <Consumo
                  etichetta={t("dashboard.usageAssistants")}
                  usato={usi.widgets_count ?? assistenti.length}
                  limite={limiti.widgets_max}
                />
              </>
            )}
          </div>
        </section>

        {/* Gli assistenti, con le stesse schede della pagina Assistenti. */}
        <section className="space-y-4">
          <TitoloSezione
            titolo={t("dashboard.assistantsTitle")}
            descrizione={t("dashboard.assistantsDescription")}
            azioni={
              assistenti.length > ASSISTENTI_IN_DASHBOARD ? (
                <Link to="/bots" className={GRIGIO}>
                  {t("dashboard.seeAllAssistants", { count: assistenti.length })}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              ) : null
            }
          />
          {showSkeleton ? (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[214px] rounded-[14px]" />
              ))}
            </div>
          ) : assistenti.length > 0 ? (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {inVista.map((bot, i) => (
                <SchedaAssistente key={bot._id} bot={bot} indice={i} />
              ))}
            </div>
          ) : !error ? (
            <BrandCard className="flex flex-col items-center px-6 py-12 text-center">
              <p className="text-base font-semibold text-[#020617]">{t("dashboard.noAssistants")}</p>
              <p className="mt-1 max-w-md text-sm text-[#64748B]">{t("dashboard.createFirstBot")}</p>
              <div className="mt-5">{creaAssistente}</div>
            </BrandCard>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default UserHome;
